package com.yendo.mx.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.yendo.mx.ui.theme.RosaYendo

// 🚀 IMPORTANTE: Asegúrate de importar el modelo que creamos
import com.yendo.mx.models.DireccionGuardada

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SelectorDireccionSheet(
    listaDirecciones: List<DireccionGuardada>,
    onDismiss: () -> Unit,
    onDireccionSeleccionada: (DireccionGuardada) -> Unit,
    onUsarUbicacionActual: () -> Unit,
    onAbrirMapa: (Int, DireccionGuardada) -> Unit,
    onEliminarDireccion: (DireccionGuardada) -> Unit // 🚀 NUEVO: Parámetro para eliminar
) {
    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    // 🚀 COLORES DARK MODE
    val isDark = isSystemInDarkTheme()
    val colorFondo = if (isDark) Color(0xFF1E1E1E) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorTextoAccion = if (isDark) Color(0xFFCCCCCC) else Color.DarkGray
    val colorIconosBase = if (isDark) Color(0xFF666666) else Color.LightGray
    val colorDivisor = if (isDark) Color(0xFF2C2C2E) else Color(0xFFF0F0F0)

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
        containerColor = colorFondo
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 20.dp, vertical = 8.dp)
                .navigationBarsPadding()
        ) {
            Text("¿Dónde entregamos?", fontWeight = FontWeight.ExtraBold, fontSize = 20.sp, color = colorTextoPrincipal)
            Spacer(Modifier.height(16.dp))

            // 1. BOTÓN "USAR MI UBICACIÓN ACTUAL"
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable {
                        onDismiss()
                        onUsarUbicacionActual()
                    }
                    .padding(vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Default.MyLocation, contentDescription = null, tint = RosaYendo, modifier = Modifier.size(24.dp))
                Spacer(Modifier.width(12.dp))
                Text("Usar mi ubicación actual", fontWeight = FontWeight.Bold, color = RosaYendo, fontSize = 15.sp)
            }

            // 2. BOTÓN "BUSCAR EN EL MAPA"
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clickable {
                        onDismiss()
                        onAbrirMapa(-1, DireccionGuardada(id = "", titulo = "", direccion = "", lat = 0.0, lng = 0.0, cp = ""))
                    }
                    .padding(vertical = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(Icons.Default.Map, contentDescription = null, tint = colorTextoAccion, modifier = Modifier.size(24.dp))
                Spacer(Modifier.width(12.dp))
                Text("Buscar en el mapa", fontWeight = FontWeight.Bold, color = colorTextoAccion, fontSize = 15.sp)
            }

            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), color = colorDivisor)

            // 3. DIRECCIONES GUARDADAS
            Text("Direcciones guardadas", fontWeight = FontWeight.Bold, fontSize = 14.sp, color = colorTextoSecundario, modifier = Modifier.padding(vertical = 8.dp))

            LazyColumn(modifier = Modifier.fillMaxWidth().heightIn(max = 300.dp)) {
                itemsIndexed(listaDirecciones) { index, dirGuardada ->

                    // 🚀 LÓGICA DE ÍCONOS INTELIGENTES
                    val iconoInteligente = when (dirGuardada.tipoUbicacion) {
                        "Casa" -> Icons.Default.Home
                        "Trabajo" -> Icons.Default.Work
                        else -> Icons.Default.Place // El pin genérico para "Otro"
                    }

                    // 🚀 LÓGICA DEL TÍTULO PRINCIPAL
                    val tituloAMostrar = if (dirGuardada.tipoUbicacion == "Otro" && dirGuardada.titulo.isNotBlank()) {
                        dirGuardada.titulo // Si eligió Otro y le puso "Casa de mi novia", mostramos eso
                    } else {
                        dirGuardada.tipoUbicacion.ifBlank { "Dirección guardada" }
                    }

                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable {
                                onDireccionSeleccionada(dirGuardada)
                                onDismiss()
                            }
                            .padding(vertical = 12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // 1. ÍCONO VISUAL DESTACADO
                        Box(
                            modifier = Modifier
                                .size(40.dp)
                                .background(RosaYendo.copy(alpha = 0.1f), RoundedCornerShape(10.dp)),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(iconoInteligente, contentDescription = null, tint = RosaYendo, modifier = Modifier.size(24.dp))
                        }

                        Spacer(Modifier.width(12.dp))

                        // 2. TEXTOS (TIPO DE LUGAR > CALLE > INSTRUCCIONES)
                        Column(modifier = Modifier.weight(1f)) {
                            Text(tituloAMostrar, fontWeight = FontWeight.Bold, fontSize = 16.sp, color = colorTextoPrincipal)
                            Text(dirGuardada.direccion, color = colorTextoSecundario, fontSize = 13.sp, maxLines = 1)

                            // Si el usuario dejó indicaciones o método de entrega, le mostramos un pequeño texto de confirmación
                            if (dirGuardada.indicaciones.isNotBlank() || dirGuardada.opcionEntrega.isNotBlank()) {
                                Text(
                                    text = "Nota: ${dirGuardada.opcionEntrega}",
                                    color = RosaYendo,
                                    fontSize = 11.sp,
                                    fontWeight = FontWeight.Medium,
                                    modifier = Modifier.padding(top = 2.dp)
                                )
                            }
                        }

                        // 3. BOTÓN EDITAR
                        IconButton(onClick = {
                            onDismiss()
                            onAbrirMapa(index, dirGuardada)
                        }) {
                            Icon(Icons.Default.Edit, contentDescription = "Editar", tint = colorIconosBase, modifier = Modifier.size(20.dp))
                        }

                        // 4. BOTÓN ELIMINAR
                        IconButton(onClick = {
                            onEliminarDireccion(dirGuardada)
                        }) {
                            Icon(Icons.Default.Delete, contentDescription = "Eliminar", tint = Color.Red.copy(alpha = 0.7f), modifier = Modifier.size(20.dp))
                        }
                    }
                }
            }
        }
    }
}